import { flushSync } from 'react-dom'

// Helpers for Formik interop

// This is the only way to set Formik state in one go and then validate.
//
// - If you are only going to change one state piece (e.g. only values, or only
//   touched, or...) it's better to use the specific Formik imperative methods
//   directly (i.e. `setValues`, `setTouched`, etc.)
//
// - If you don't need validation better use `formik.setFormikState` directly.
export const setFormikStateAndValidate = async (formik, updateFn) => {
  // HACK! `flushSync` is an undocumented function (uh-oh) that forces the
  // internal Formik reducer to process all `dispatch` calls
  //
  // If we don't do this, `setFormikState` is not exactly synchronous (I guess
  // due to batching of updates), and `formik.validateForm` validates with the
  // OLD values.
  //
  // Another way to do this is to `setTimeout(validateForm, 0)`, but this led
  // to a small flash of errors between the `setFormikState` call and the
  // posterior validation.
  //
  // Even though this forces React tasks to happen (not ideal), it's still
  // better than having two Formik state changes (e.g. `setValues` then
  // `setTouched`) by around 20% performance.
  flushSync(() => {
    formik.setFormikState(state => ({ ...state, ...updateFn(state) }))
  })

  await formik.validateForm()
}

// This is needed because Formik touches all fields on submit, but it touches
// all nested objects and arrays, regardless of their actual type
//
// With this we can generate a `touched` object that only touches nested fields
// specified as nested in our proptypes
//
// This is the culprit in Formik:
// https://github.com/formium/formik/blob/903c1101240632c84fa36c8991ea9fd03b25f1cf/packages/formik/src/Formik.tsx#L98-L101
export const generateTouchedObject = (fieldPropTypes, values) =>
  generateTouchedField(
    {
      type: 'shape',
      shape: fieldPropTypes,
    },
    values,
  )

const generateTouchedField = (propType, values) => {
  // Base case: a normal proptype
  if (!propType.type) {
    return true
  }

  // Our custom propType DSL (see formik-prop-types.js for info)
  switch (propType.type) {
    case 'shape': {
      return Object.fromEntries(
        Object.entries(propType.shape).map(([field, propType]) => [
          field,
          generateTouchedField(propType, values?.[field]),
        ]),
      )
    }

    case 'arrayOf': {
      return (
        values?.map((_, index) =>
          generateTouchedField(propType.of, values[index]),
        ) ?? []
      )
    }

    default: {
      throw new Error('Unknown custom propType')
    }
  }
}
